import { Trans } from "@lingui/react/macro";
import { OutTotoEntry } from "../../../services/totocalcio/types";
import { ComponentChildren } from "preact";
import { TotoEvent, TotoTicket } from "../../../services/totocalcio/totocalcio-service";
import { RegionFlagIcon } from "./prematch-menu";
import MoneyFormatter from "../utils/money-formatter";

export function Tab({ children, onClick, active }: { children: ComponentChildren, onClick: () => void, active: boolean }) {
    if (active)
        return <div onClick={onClick} class="relative text-white font-bold border-b-4 border-amber-500 pb-2 whitespace-nowrap">{children}</div>
    return <span onClick={onClick} class="hover:text-white text-gray-400 font-semibold cursor-pointer pb-2 whitespace-nowrap">{children}</span>
}

export function TournamentFilter({ children }: { children: ComponentChildren }) {
    return (
        <div class="pb-1">
            <nav class="flex items-center justify-between px-6 py-4 bg-harif-900 border-b border-b-harif-950 max-w-full overflow-x-auto">
                <div class="flex gap-x-8 sm:px-8 text-sm uppercase tracking-wider">
                    {children}
                </div>
                <span class="hidden sm:block">
                    <ShieldIcon />
                </span>
            </nav>
        </div>
    );
}

export function ShieldIcon() {
    return <div class="text-amber-500">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
    </div>
}

export function TournamentPreviewWidget({ tournament, onClick }: { tournament: OutTotoEntry, onClick: () => void }) {
    return (
        <div class="overflow-hidden shadow-md border border-harifzinc-800 bg-harifzinc-900 flex flex-col">
            {/* Banner Section */}
            <TotoBanner bgUrl={tournament.tournament.bannerUrl} />

            {/* Info Section */}
            <div class="p-4 flex flex-col justify-between grow">
                <div class="flex justify-between items-start pb-4">
                    <div>
                        <p class="text-xs text-gray-400 font-bold uppercase tracking-wider pb-1">
                            <span class="block">Start: {new Date(tournament.tournament.startDate).toLocaleDateString()}</span>
                            <span class="block">End: {new Date(tournament.tournament.endDate).toLocaleDateString()}</span>
                        </p>
                        <h2 class="text-base font-black text-white pb-1 leading-tight uppercase">{tournament.tournament.name}</h2>
                        <h3 class="text-sm font-bold text-amber-500">
                            <MoneyFormatter value={tournament.tournament.displayPrizePool} currency={tournament.tournament.displayCurrency} />
                        </h3>
                        <p class="text-xs text-gray-400 font-medium pt-1">
                            {tournament.tournament.marketName} | {tournament.tournament.numEvents} <Trans>Events</Trans> | <MoneyFormatter value={tournament.tournament.displayCost} currency={tournament.tournament.displayCurrency} />
                        </p>
                    </div>
                    <div class="text-right flex flex-col items-end">
                        <p class="text-xs text-gray-400 font-semibold pb-2">
                            <Trans>Closes</Trans>: <span class="text-white">{new Date(tournament.tournament.startDate).toLocaleDateString()}</span>
                        </p>
                        <button class="bg-harifzinc-800 hover:bg-harifzinc-700 text-white text-xs font-bold py-1.5 px-3 flex items-center border border-harifzinc-700">
                            <Trans>Info</Trans>
                            <div class="pl-1.5">
                                <span class="bg-amber-500 text-black rounded-full w-3.5 h-3.5 flex items-center justify-center text-xs">i</span>
                            </div>
                        </button>
                    </div>
                </div>
                {/* CTA nello stile Harif (amber su nero) */}
                <button class="w-full bg-amber-500 hover:brightness-110 py-2.5 text-sm text-black font-black uppercase tracking-widest"
                    onClick={onClick}>
                    <Trans>View Jackpot</Trans>
                </button>
            </div>
        </div>
    );
}

export function TotoBanner({ bgUrl }: { bgUrl: string }) {
    return <div class="relative w-full h-40 bg-cover bg-center border-b border-harifzinc-800" style={{ backgroundImage: `url(${bgUrl})` }}></div>
}

export function TotoSettlementsPage({ tournamentInfo, partite }: { partite: TotoEvent[], tournamentInfo: OutTotoEntry }) {
    return <div class="text-sm">
        <div class="jackpot-item">
            <div class="jackpot-body">
                <div class="single-jackpot-all">
                    <TotoDayTitle tournamentInfo={tournamentInfo} />
                    <div class="events-all opened flex flex-col overflow-hidden bg-harif-900 border-x border-b border-harif-950">
                        {partite.sort((a, b) => a.time.getTime() - b.time.getTime()).map(partita => <TotoSettlementRow match={partita} />)}
                    </div>
                </div>
            </div>
        </div>
    </div>
}

/* Intestazione stile Harif — dark con accent amber */
export function TotoDayTitle({ tournamentInfo }: { tournamentInfo: OutTotoEntry }) {
    return (<div class="jackpot-all-head opened bg-harif-800 border-b border-b-harif-950 text-white cursor-pointer px-4 py-3 flex justify-between items-center">
        <div class="flex items-center">
            <i class="icon-chevron_down icon pr-2"></i>
            <div>
                <div class="font-black text-sm uppercase tracking-wide text-amber-500">{tournamentInfo.tournament.name}</div>
                <div class="text-xs font-semibold pt-0.5 text-gray-300">
                    <span><Trans>Prizepool</Trans>: </span>
                    <MoneyFormatter value={tournamentInfo.tournament.displayPrizePool} currency={tournamentInfo.tournament.displayCurrency} />
                </div>
            </div>
        </div>
        <div class="flex items-center text-xs font-bold bg-harifzinc-950 border border-harifzinc-800 px-2 py-1">
            <Trans>Finished on</Trans>:&nbsp;<span class="font-normal text-gray-300">{new Date(tournamentInfo.tournament.endDate).toLocaleString()}</span>
        </div>
    </div>);
}

export function TotoSettlementRow({ match }: { match: TotoEvent }) {
    return <div class="h-10 flex justify-between px-4 border-b border-b-harif-950 text-xs items-center relative text-white bg-harif-900 hover:bg-harif-800">
        <div class="text-xs font-mono font-bold text-gray-500 w-10">{match.code.split('/')[2]}</div>
        <div class="jackpot-list-middle-section flex text-sm box-border grow items-center">
            <span class="jackpot-team-home flex pr-4 text-end justify-end font-bold w-1/2 min-w-0">
                {match.home}
            </span>
            <div class="flex justify-center items-center font-bold bg-harifzinc-800 border border-harifzinc-700 text-amber-500 w-16 h-6 shadow-inner">
                {match.result}
            </div>
            <span class="jackpot-team-away font-bold pl-4 flex justify-start w-1/2 min-w-0">
                {match.away}
            </span>
        </div>
        <div class="desktop-date-jackpot w-24 text-right text-xs text-gray-500 font-semibold uppercase">
            <span><Trans>Full Time</Trans></span>
        </div>
    </div>
}

export function TotoRandomPicker({ ticketApi }: { ticketApi: TotoTicket }) {
    return (
        <div class="bg-harif-800 border-b border-b-harif-950 px-4 py-3 flex justify-between items-center">
            <div class="text-gray-300 text-sm font-semibold">
                <span><Trans>Let the quick pick randomly choose the Jackpot selections for you!</Trans></span>
            </div>
            <div class="flex">
                <QuickplayButton onClick={() => ticketApi.randomPick()} />
            </div>
        </div>)
}

export function QuickplayButton({ onClick }: { onClick? }) {
    return <button class="quickpick-button px-6 py-2 text-sm font-black text-black uppercase tracking-wide bg-amber-500 hover:brightness-110" onClick={onClick}>
        <span><Trans>Quick Pick</Trans></span>
    </button>
}

export function TotoOutcomeTd({ children }) {
    return <div class="font-bold text-xs uppercase w-full text-center pb-1 text-white">
        {children}
    </div>
}

export function TotoEventTh({ children }: { children: ComponentChildren }) {
    return (
        <div class="event-row-header py-2 sm:px-3 bg-harif-900 border-t border-b border-t-harif-800 border-b-harif-950 text-white">
            <div class="flex flex-nowrap justify-between items-center">
                <div class="hidden sm:block w-16 shrink-0 text-center font-bold text-xs">ID</div>
                <div class="hidden sm:block w-14 shrink-0 text-center font-bold text-xs">Day</div>
                <div class="hidden sm:block w-12 shrink-0 text-center font-bold text-xs">Hour</div>
                <div class="flex-grow"></div>
                <div class="flex w-52 sm:w-60 justify-between items-center">
                    <div class="flex flex-nowrap w-full justify-around">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}

export function TotoEventRowWrapper({ children }: { children: ComponentChildren }) {
    return <div class="border-b border-b-harif-950 border-t border-t-harif-800 bg-harif-900 hover:bg-harif-800">
        {children}
    </div>
}

export function TotoEventRow({ children }: { children: ComponentChildren }) {
    return <div class="flex flex-nowrap items-stretch sm:px-3 py-1">
        {children}
    </div>
}

export function TotoEventDiv({ match }: { match: TotoEvent }) {
    return <>
        {/* Desktop */}
        <div class="hidden sm:flex flex-nowrap flex-grow items-center overflow-hidden text-white">
            <div class="w-16 text-center text-xs font-mono text-gray-400">{match.code.split('/')[2]}</div>
            <div class="w-14 text-center text-xs font-mono text-gray-400">{new Date(match.time).toLocaleDateString([], { day: "numeric", month: 'numeric' })}</div>
            <div class="w-12 text-center text-xs font-mono text-gray-400">{new Date(match.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            <div class="flex gap-2">
                <RegionFlagIcon category={match.parent.parent} />
                <div class="font-bold text-sm leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                    {match.home} - {match.away}
                </div>
            </div>
        </div>
        {/* Mobile */}
        <div class="flex sm:hidden flex-nowrap grow min-w-0 items-center overflow-hidden text-white">
            <div class="flex text-xs flex-col w-10 shrink-0 items-center text-gray-400">
                <span class="flex"><RegionFlagIcon category={match.parent.parent} /></span>
                <span class="block">{new Date(match.time).toLocaleDateString([], { day: "numeric", month: 'numeric' })}</span>
                <span class="block">{new Date(match.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div class="flex flex-col gap-1 overflow-hidden min-w-0">
                <div class="font-bold text-sm leading-tight whitespace-nowrap overflow-hidden truncate">
                    {match.home}
                </div>
                <div class="font-bold text-sm leading-tight whitespace-nowrap overflow-hidden truncate">
                    {match.away}
                </div>
            </div>
        </div>
    </>
}

export function TotoOddsContainer({ children }: { children: ComponentChildren }) {
    return <div class="flex flex-nowrap items-center shrink-0 w-52 sm:w-60 justify-between gap-1">
        {children}
    </div>
}

export function ButtonOdds(
    { odd, ticketService, children }:
    { odd: TotoEvent['sub'][string]['sub'][string], ticketService: { toggleOdd: () => void, isSelected: boolean }, children: ComponentChildren }
) {
    return <div
        onClick={e => ticketService.toggleOdd()}
        class="flex-1 h-9 flex flex-col items-center justify-center cursor-pointer border-l border-r border-l-harif-950 border-r-harif-800 font-bold text-sm gap-0.5
               bg-harif-900 text-amber-500
               hover:brightness-110
               data-[selected=true]:bg-amber-500 data-[selected=true]:text-black"
        data-selected={ticketService.isSelected}
    >
        {children}
    </div>
}